import { Button } from 'primereact/button'

import { PermissionSuffix } from '../../_generated/api/user-management'
import { useUserContext } from '../contexts/UserContext'
import { useProjectContext } from '../contexts/ProjectContext'
import { useExportContext } from '../contexts/ExportContext'
import { useStructureContext } from '../contexts/StructureContext'
import { useArchiveContext } from '../contexts/ArchiveContext'
import { ContentCell, LayoutGridContainer } from '../common/pure/layout'
import { EntryCard } from '../common/EntryCard'
import StructureScreen from './StructureScreen'
import FullScreenLoading from '../common/FullScreenLoading'
import ToastOnce from '../common/ToastOnce'
import {
    getMenuItemArchive,
    getMenuItemBoltConnection,
    getMenuItemProject,
    getMenuItemSeparator,
    getMenuItemExport,
} from '../common/menu-items'
import CustomIcon from '../common/pure/CustomIcon'
import { FolderIcon } from '../common/inline-svg'
import { pi } from '../common/pure/PrimeIcon'
import MessageScreen, { ErrorMessageScreen } from '../common/pure/MessageScreen'
import { StructureTypes } from '../../models/Structure'
import { useArchiveEntryAndNavigate, useCreateUnnamedEntryAndNavigate } from '../../hooks/crud'
import MenuBuilder from '../../tools/MenuBuilder'
import t from '../../tools/t'

export default function ProjectScreen() {
    const { can } = useUserContext()
    const { exportProject } = useExportContext()
    const { restoreProject, structure, structureLoading } = useStructureContext()
    const { qr, selectedProject, selectedProjectLoading, selectedProjectError, updateProject } =
        useProjectContext()
    const { includeArchived } = useArchiveContext()
    const createAndNavigate = useCreateUnnamedEntryAndNavigate()
    const archiveAndNavigate = useArchiveEntryAndNavigate()
    const canWriteStructure = can(PermissionSuffix.StructureWrite)

    const submitNameChange = async (name: string) => {
        const project = await updateProject({ name })
        return !!project
    }

    const onAddProject = () =>
        selectedProject && createAndNavigate(selectedProject, StructureTypes.Project)
    const onAddBoltConnection = () =>
        selectedProject && createAndNavigate(selectedProject, StructureTypes.BoltConnection)

    const archiveProjectAndNavigate = async () => {
        if (!selectedProject) return
        await archiveAndNavigate(selectedProject, StructureTypes.Project)
    }

    const onRestoreProject = async () => {
        if (!selectedProject) return
        await restoreProject(selectedProject.id)
    }

    if (!selectedProject) {
        if (selectedProjectLoading) return <FullScreenLoading />
        return (
            <ErrorMessageScreen
                title={selectedProjectError?.name}
                message={selectedProjectError?.message}
            />
        )
    }

    const selectedEntry = structure.findEntry(selectedProject.id)
    if (!selectedEntry) {
        if (structureLoading) return <FullScreenLoading />
        return <ErrorMessageScreen />
    }

    const isArchived = !!selectedProject?.isArchived
    const children = selectedEntry.children.filter((entry) => includeArchived || !entry.isArchived)
    const parent = selectedEntry.parentId ? structure.findEntry(selectedEntry.parentId) : undefined

    return (
        <StructureScreen
            node={selectedProject}
            parent={parent}
            ancestors={selectedProject?.ancestors ?? []}
            onSubmitNameChange={canWriteStructure ? submitNameChange : undefined}
            onRestore={canWriteStructure ? onRestoreProject : undefined}
            qr={qr}
            customActionTray={
                <>
                    {canWriteStructure && (
                        <Button
                            severity="secondary"
                            outlined
                            disabled={isArchived}
                            onClick={onAddBoltConnection}
                        >
                            <CustomIcon name="bolt" margin />
                            {t('bolt_connection_create')}
                        </Button>
                    )}
                    {canWriteStructure && (
                        <Button
                            severity="secondary"
                            icon={pi('folder')}
                            label={t('project_create')}
                            outlined
                            disabled={isArchived}
                            onClick={onAddProject}
                        />
                    )}
                </>
            }
            menu={new MenuBuilder()
                .addIf(
                    getMenuItemProject(onAddProject, t('project_create')),
                    canWriteStructure && !isArchived
                )
                .addIf(
                    getMenuItemBoltConnection(onAddBoltConnection, t('bolt_connection_create')),
                    canWriteStructure && !isArchived
                )
                .add(
                    getMenuItemExport(() => exportProject(selectedProject?.id), t('project_export'))
                )
                .addIf(getMenuItemSeparator(), canWriteStructure && !isArchived)
                .addIf(
                    getMenuItemArchive(archiveProjectAndNavigate, t('project_delete')),
                    canWriteStructure && !isArchived
                )
                .toArray()}
        >
            {selectedProject && (
                <LayoutGridContainer grow={false}>
                    {children?.length ? (
                        children?.map((projectEntry) => (
                            <ContentCell key={projectEntry.id} columns={1}>
                                <EntryCard entry={projectEntry} />
                            </ContentCell>
                        ))
                    ) : (
                        <MessageScreen
                            title={t('project_empty')}
                            message={t('project_empty_message')}
                            graphic={<FolderIcon />}
                        />
                    )}
                </LayoutGridContainer>
            )}
            <ToastOnce key="selectedProjectError" error={selectedProjectError} />
        </StructureScreen>
    )
}
